import { useEffect, useMemo, useRef, useState } from "react";

// DEPENDENCIES
import { useFormik } from "formik";
import isEqual from "lodash/isEqual";

// UTILS
import {
  generateYupSchema,
  getKeys,
  getNestedValue,
  getRequiredFieldNames,
  getShouldFieldRender,
  getTouchedFromErrors,
  groupFieldsIntoRows,
  isEmptyObject,
  scrollFormToTop,
} from "./utils";
import {
  onUpdatePrimaryButtonModal,
  openModal,
} from "../../reducers/modalReducer";
import {
  onUpdateIsEditing,
  onUpdateIsFormOpen,
} from "../../reducers/collectionReducer";
import {
  onUpdatePrimaryButton,
  onUpdateSecondaryButton,
  onUpdateTertiaryButton,
} from "../../reducers/buttonsReducer";

// HOOKS
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import useAlertFeedback from "../../hooks/useAlertFeedback";
import useWindowWidth from "../../hooks/useWindowWidth";

// TYPES
import { SecondaryForm } from "../../types";

// COMPONENTS
import { Button } from "@allaria-tech/matter/Button";
import { Feedback } from "@allaria-tech/matter/Feedback";
import { Form } from "@allaria-tech/matter/Form";
import { FormRow } from "@allaria-tech/matter/FormRow";
import { Icon } from "@allaria-tech/matter/Icon";
import { IconButton } from "@allaria-tech/matter/IconButton";
import { SegmentedControl } from "@allaria-tech/matter/SegmentedControl";
import { PageHeader } from "@allaria-tech/matter/PageHeader";
import { Typography } from "@allaria-tech/matter/Typography";
import Container from "../Container/Container";
import TemplateInputSelector from "./TemplateInputSelector";
import SecondaryFormSuccess from "./SecondaryFormSuccess";

import styles from "../TemplateInterpreter/TemplateInterpreter.module.scss";

const DELETE_ENTITY_MODAL_ID = "delete-entity";

const TemplateInterpreterSecondaryForm = ({
  collectionName,
  inline,
  tabs,
  fields,
  onSave,
  alert,
  primaryForm,
  title,
  mobileTitle,
  mobileEditTitle,
  mobileSections,
  deleteEntity,
  showErrorsOnOpen,
  onSuccessCondition,
  onSuccessContent,
}: SecondaryForm) => {
  const {
    formContainer,
    formRow,
    secondaryForm,
    secondaryFormHeader,
    secondaryFormPageHeader,
    secondaryFormActions,
    secondaryFormFields,
    inlineForm,
  } = styles;

  const { setValues: setPrimaryFormValues } = primaryForm || {};

  const dispatch = useAppDispatch();

  const state = useAppSelector((state) => state);
  const { formValues } = state.onboardingData;
  const { activeIndex } = state.collectionData;

  const { width } = useWindowWidth();
  const isMobile = width < 768;
  const isSmallMobile = width < 400;

  const collection = useMemo(
    () => formValues?.[collectionName] || {},
    [formValues, collectionName],
  );

  const activeData = useMemo(
    () => (activeIndex && collection[activeIndex - 1]) || {},
    [collection, activeIndex],
  );

  const [activeTab, setActiveTab] = useState<string>(
    tabs?.active(activeData) || "",
  );

  const [requestedSection, setRequestedSection] = useState(0);
  const formRef = useRef<HTMLFormElement | null>(null);

  const initialsecondaryFormValues = fields.reduce(
    (acc: Record<string, string>, { name, initialValue }) => {
      acc[name] = initialValue.toString();
      return acc;
    },
    {},
  );

  const secondaryFormFormik = useFormik({
    initialValues: initialsecondaryFormValues,
    validationSchema: generateYupSchema(fields, state),
    onSubmit: (_, { setSubmitting }) => {
      setSubmitting(false);
    },
    // So the validation is triggered on mount to update isValid and disable the button until all the fields are valid
    // If not, the button will be enabled by default
    // The invalid styles of the fields will not be shown until the touched state is true
    validateOnMount: true,
  });

  const {
    values,
    setValues,
    resetForm,
    setTouched,
    errors,
    setErrors,
    isSubmitting,
    setSubmitting,
    validateForm,
  } = secondaryFormFormik;

  const requiredFieldsNames = useMemo(() => {
    return getRequiredFieldNames(fields, state, values, dispatch);
  }, [fields, values]);

  const requiredFieldsHaveErrors =
    getKeys(errors).filter((key) => requiredFieldsNames?.includes(key)).length >
    0;
  const requiredFieldsHaveValues = requiredFieldsNames.every((key) => {
    return getNestedValue(values, key);
  });

  const handleActiveTabChange = (tab: string) => {
    tabs?.onChange(tab, setActiveTab, dispatch, state, values);
    setValues((prev) => ({ ...prev, activities: "" }), false);
    setTouched({}, false);
    setErrors({});
    setRequestedSection(0);
  };

  // Formik marks a field as touched as soon as it loses focus, even when
  // nothing was typed, so saving follows the values instead: it is only allowed
  // when they differ from the entity already stored in the collection
  const hasChanges = useMemo(
    () =>
      fields.some(
        ({ name }) =>
          !isEqual(
            getNestedValue(values, name) ?? "",
            getNestedValue(activeData, name) ?? "",
          ),
      ),
    [fields, values, activeData],
  );

  const isSaveButtonDisabled =
    !hasChanges ||
    !requiredFieldsHaveValues ||
    requiredFieldsHaveErrors ||
    isSubmitting;

  const showDeleteEntity = !!deleteEntity && !deleteEntity.hidden?.(state);
  const isDeleteEntityDisabled = !!deleteEntity?.disabled?.(state);

  // An entity with nothing stored has no data to lose, so it is removed without
  // asking for confirmation
  const isActiveEntityEmpty = useMemo(
    () => fields.every(({ name }) => !getNestedValue(activeData, name)),
    [fields, activeData],
  );

  const pageHeaderTitle =
    (isActiveEntityEmpty ? mobileTitle : mobileEditTitle || mobileTitle) ||
    (activeIndex && title ? title(activeIndex) : "");

  const customAlert = useAlertFeedback(alert?.id || "");
  const deleteEntityAlert = useAlertFeedback(DELETE_ENTITY_MODAL_ID);

  const handleSubmit = () => {
    onSave(values, dispatch, state, setPrimaryFormValues, secondaryFormFormik);
    setSubmitting(false);
    // Saving lands back on the list, where the entity now has its own card
    if (isMobile) dispatch(onUpdateIsFormOpen(false));
  };

  const handleDeleteEntity = () => {
    if (!deleteEntity) return;

    // The removed entity leaves its data behind in the form, which no longer
    // belongs to any card: it is cleared, and on mobile the list comes back
    const removeEntity = () => {
      deleteEntity.onClick(dispatch, state, setPrimaryFormValues);
      resetForm();
      if (isMobile) dispatch(onUpdateIsFormOpen(false));
    };

    if (isActiveEntityEmpty) return removeEntity();

    dispatch(openModal({ name: DELETE_ENTITY_MODAL_ID }));
    dispatch(
      onUpdatePrimaryButtonModal({
        text: "Sí, eliminar",
        action: removeEntity,
      }),
    );
  };

  const visibleFields = useMemo(
    () =>
      fields.filter((field) =>
        getShouldFieldRender(field, values, dispatch, state),
      ),
    [fields, values, state, dispatch],
  );

  // Mobile walks one titled section at a time. A section with nothing to show
  // is dropped — the juridical tab has no address to ask for — and fields no
  // section claims are collected by the last one, so nothing becomes
  // unreachable and blocks the save
  const sections = useMemo(() => {
    const configuredSections = mobileSections?.(activeTab);

    if (!isMobile || !configuredSections?.length) return undefined;

    const claimed = configuredSections.flatMap(({ fields: names }) => names);

    const grouped = configuredSections.map(({ title, fields: names }) => ({
      title,
      fields: visibleFields.filter(({ name }) => names.includes(name)),
    }));

    const unclaimed = visibleFields.filter(
      ({ name }) => !claimed.includes(name),
    );

    grouped[grouped.length - 1].fields.push(...unclaimed);

    return grouped.filter(({ fields: sectionFields }) => sectionFields.length);
  }, [isMobile, mobileSections, activeTab, visibleFields]);

  const sectionIndex = Math.min(
    requestedSection,
    Math.max((sections?.length ?? 1) - 1, 0),
  );
  const currentSection = sections?.[sectionIndex];
  const hasSections = !!sections;
  const isLastSection = !sections || sectionIndex === sections.length - 1;

  const currentSectionFieldNames = currentSection?.fields.map(
    ({ name }) => name,
  );

  const isCurrentSectionIncomplete =
    !!currentSectionFieldNames &&
    (currentSectionFieldNames.some(
      (name) =>
        requiredFieldsNames.includes(name) && !getNestedValue(values, name),
    ) ||
      getKeys(errors).some((key) =>
        currentSectionFieldNames.some(
          (name) => key === name || key.startsWith(`${name}.`),
        ),
      ));

  const goToSection = (index: number) => {
    setRequestedSection(index);
    scrollFormToTop(formRef.current);
  };

  // Fields are grouped into rows so the ones sharing a `columns` value sit side
  // by side, the same way the primary form lays them out
  const fieldRows = useMemo(
    () => groupFieldsIntoRows(currentSection?.fields ?? visibleFields),
    [currentSection, visibleFields],
  );

  useEffect(() => {
    setRequestedSection(0);
  }, [activeIndex]);

  useEffect(() => {
    if (isEmptyObject(activeData)) return;

    setValues(activeData, false);
    setTouched({}, false);
    setErrors({});

    let isCurrentEntity = true;

    // Loading the entity skips validation to keep its fields quiet, which
    // leaves behind the errors `validateOnMount` found on the empty initial
    // values: they are refreshed here against what was actually loaded, or an
    // entity that is already complete reads as invalid until a field is touched
    validateForm(activeData).then((validationErrors) => {
      if (!isCurrentEntity || isEmptyObject(validationErrors)) return;
      if (!showErrorsOnOpen?.(activeData)) return;

      setTouched(getTouchedFromErrors(validationErrors), false);
    });

    return () => {
      isCurrentEntity = false;
    };
  }, [activeData]);

  useEffect(() => {
    if (isEmptyObject(values)) resetForm();
  }, [values]);

  useEffect(() => {
    if (tabs) setActiveTab(tabs.active(activeData));
  }, [activeData]);

  useEffect(() => {
    if (alert)
      dispatch(
        onUpdatePrimaryButtonModal({
          text: alert.primaryButton.text,
          action: () => alert.primaryButton.action(activeTab, dispatch, state),
        }),
      );
  }, [activeData, activeTab]);

  useEffect(() => {
    dispatch(onUpdateIsEditing(!isSaveButtonDisabled));
  }, [isSaveButtonDisabled]);

  const closeForm = () => dispatch(onUpdateIsFormOpen(false));

  // Mobile has no room for the card's own header actions, so the footer group
  // owns saving and the Page Header owns going back to the list
  useEffect(() => {
    if (!isMobile) return;

    dispatch(
      onUpdatePrimaryButton(
        isLastSection
          ? {
              text: hasSections ? "Guardar cambios" : "Guardar",
              disabled: isSaveButtonDisabled,
              isLoading: isSubmitting,
              onClick: handleSubmit,
              show: true,
            }
          : {
              text: "Siguiente",
              disabled: isCurrentSectionIncomplete,
              isLoading: false,
              onClick: () => goToSection(sectionIndex + 1),
              show: true,
            },
      ),
    );
    dispatch(
      onUpdateSecondaryButton({
        text: "Volver",
        disabled: false,
        isLoading: false,
        onClick:
          sectionIndex > 0 ? () => goToSection(sectionIndex - 1) : closeForm,
        show: true,
      }),
    );
    dispatch(onUpdateTertiaryButton({ show: false }));
    // Every dependency has to be a primitive: this effect writes to the store
    // the whole component reads from, so an identity that changes on each
    // render — `sections` rebuilds from `state` — would loop it
  }, [
    isMobile,
    isSaveButtonDisabled,
    isSubmitting,
    values,
    hasSections,
    sectionIndex,
    isLastSection,
    isCurrentSectionIncomplete,
  ]);

  const content =
    onSuccessCondition && onSuccessContent && onSuccessCondition(state) ? (
      <SecondaryFormSuccess
        title={onSuccessContent.title}
        subtitle={onSuccessContent.subtitle}
        message={onSuccessContent.message}
      />
    ) : (
      <>
        {isMobile ? (
          <PageHeader
            className={secondaryFormPageHeader}
            title={pageHeaderTitle}
            onBack={closeForm}
            sticky={false}
            extraActions={
              showDeleteEntity ? (
                <IconButton
                  variant="ghost"
                  aria-label="Eliminar"
                  icon={<Icon name="trash-can" size="sm" />}
                  onClick={handleDeleteEntity}
                  disabled={isDeleteEntityDisabled}
                />
              ) : undefined
            }
          />
        ) : (
          <div className={secondaryFormHeader}>
            {activeIndex && title && (
              <Typography type="HEADING" size="M">
                {title(activeIndex)}
              </Typography>
            )}
            <div className={secondaryFormActions}>
              {showDeleteEntity && (
                <IconButton
                  variant="outline"
                  size="small"
                  aria-label="Eliminar"
                  icon={<Icon name="trash-can" size="sm" />}
                  onClick={handleDeleteEntity}
                  disabled={isDeleteEntityDisabled}
                />
              )}
              <Button
                text="Guardar"
                type="submit"
                size="small"
                onClick={handleSubmit}
                isLoading={isSubmitting}
                isDisabled={isSaveButtonDisabled}
              />
            </div>
          </div>
        )}
        {tabs && (
          <SegmentedControl
            size="large"
            width="fill"
            value={activeTab}
            items={tabs.options.map(({ id, label, leadingIcon }) => ({
              value: id,
              label,
              leadingIcon: isSmallMobile ? undefined : leadingIcon,
            }))}
            onValueChange={handleActiveTabChange}
            disabled={tabs.disabled ? tabs.disabled(state) : false}
          />
        )}
        {currentSection && (
          <Typography type="HEADING" size="M">
            {currentSection.title}
          </Typography>
        )}
        {alert && (
          <Feedback
            {...customAlert}
            type="WARNING"
            title={alert.title}
            description={alert.message}
          />
        )}
        {deleteEntity && (
          <Feedback
            {...deleteEntityAlert}
            type="WARNING"
            title={(deleteEntity.alert?.title(activeData) || "") as string}
            description={
              deleteEntity.alert?.message || "Se descartarán sus datos."
            }
          />
        )}
        <form
          ref={formRef}
          className={inline ? "" : `${secondaryFormFields} scrollbar`}
        >
          <Form className={formContainer}>
            {fieldRows.map((row, index) => (
              <FormRow key={index} columns={row.columns} className={formRow}>
                {row.fields.map((field) => (
                  <TemplateInputSelector
                    currentStepFields={fields}
                    key={field.name}
                    field={field}
                    formik={secondaryFormFormik}
                  />
                ))}
              </FormRow>
            ))}
          </Form>
        </form>
      </>
    );

  if (inline) return <div className={inlineForm}>{content}</div>;

  return (
    <Container className={`flex-col ${secondaryForm}`}>{content}</Container>
  );
};

export default TemplateInterpreterSecondaryForm;
